import '../../shared/style.css';
import { Button, Group, Modal, ModalDrawerControllerProps, ModalProps } from '@economic/gaudi';
import React from 'react';
import { Field } from '../../../../../shared/components/Form';
import { t } from '../../../../../shared/components/l10n/l10n';
import { DisplayMessages } from '../../shared/components/DisplayMessages';
import { InvoicingMessage, InvoicingMessageType, SendByType } from '../../invoices/book-and-send/types';
import { useEmailOptionsPdfSelected } from '../../invoices/utils';
import { SendAsEmail } from '../../shared/components/SendBy';
import { EmailOptionsDrawerToggle } from '../../shared/components/EmailOptionsDrawerToggle';
import { SalesModalFormWithEmailOptionsDrawer } from '../../shared/components/SalesModalFormWithEmailOptionsDrawer';
import { BulkBookOnlyReminderConfirmationDialogContent } from './BulkBookOnlyReminderConfirmationDialogContent';
import { EmailTemplatesFormState } from '../../invoices/utils/emailTemplateFormState';
import { EmailTemplatePicker } from '../../invoices/utils/EmailTemplatePicker';
import { emailTemplateUpdater } from '../../invoices/utils/emailTemplateUpdater';

export type BulkBookAndSendReminderFormData = {
    skipBookOnlyConfirmation: boolean;
    bookOnly: boolean;
    isBatched: boolean;
    bookAllBatches: boolean;
    batchSize: number;
    remindersToSendCount: number;
    customerIdBatches: number[][];
    emailOptions: {
        showPdf: boolean;
        subject: string;
        saveSubject: boolean;
        body: string;
        saveBody: boolean;
        includePdfLink: boolean;
        encrypt: boolean;
        includePdfAsAttachment: boolean;
    };
} & EmailTemplatesFormState;

function useRequiredFieldsValidation(): InvoicingMessage[] {
    const requiredFieldsMessages = [] as InvoicingMessage[];

    const isPdfSelected = useEmailOptionsPdfSelected();

    if (!isPdfSelected) {
        requiredFieldsMessages.push({ message: t`FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR`, type: InvoicingMessageType.Error });
    }

    return requiredFieldsMessages;
}

type BulkBookAndSendReminderDialogProps = Pick<ModalProps, 'disclosure'> & {
    formData?: BulkBookAndSendReminderFormData;
    loading: boolean;
    onBookAndSend: (values: BulkBookAndSendReminderFormData) => Promise<any>;
    onHide: () => void;
    error: boolean;
};

export function BulkBookAndSendReminderDialog(props: BulkBookAndSendReminderDialogProps) {
    const { formData, loading, onBookAndSend, onHide, disclosure, error } = props;

    let errorMessages: InvoicingMessage[] = error
        ? [{ type: InvoicingMessageType.Error, message: t`FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR` }]
        : [];

    function bookAndSend(form: any, bookOnly: boolean) {
        form.change('bookOnly', bookOnly);
        form.change('sendByType', Number(SendByType.email));
        form.blur('sendByType');
        form.submit();
    }

    function updater() {
        return [emailTemplateUpdater()];
    }

    return (
        <SalesModalFormWithEmailOptionsDrawer
            hideRecipientsSection={true}
            loading={loading}
            errorTitle={t`FORM_BOOKANDSEND_ERRORS_HEADER`}
            errorMessages={errorMessages}
            ariaLabel={t(`FORM_BOOKANDSEND_REMINDER_HEADER`)}
            disclosure={disclosure!}
            onHide={onHide}
            className="book-and-send-dialog form"
            size="xs"
            initialData={formData}
            onSubmit={onBookAndSend}
            updates={updater}>
            {(form, drawer) =>
                formData && (
                    <BulkBookAndSendReminderDialogContent
                        formData={formData}
                        drawer={drawer}
                        onBookAndSend={bookOnly => bookAndSend(form, bookOnly)}
                    />
                )
            }
        </SalesModalFormWithEmailOptionsDrawer>
    );
}

type BulkBookAndSendReminderDialogContentProps = {
    formData: BulkBookAndSendReminderFormData;
    drawer: ModalDrawerControllerProps;
    onBookAndSend: (bookOnly: boolean) => void;
};

function BulkBookAndSendReminderDialogContent(props: BulkBookAndSendReminderDialogContentProps) {
    const [showBookOnlyConfirmation, setBookOnlyConfirmation] = React.useState<boolean>(false);
    const { formData, drawer, onBookAndSend } = props;
    const requiredFieldValidationMessages = useRequiredFieldsValidation();

    function onBookOnlyAction() {
        if (formData.skipBookOnlyConfirmation) {
            onBookAndSend(true);
        } else {
            if (drawer.visible) {
                drawer.hide();
            }

            setBookOnlyConfirmation(true);
        }
        if (drawer.visible) {
            drawer.hide();
        }
    }

    if (showBookOnlyConfirmation) {
        return (
            <BulkBookOnlyReminderConfirmationDialogContent
                onBack={() => setBookOnlyConfirmation(false)}
                onOk={() => onBookAndSend(true)}
            />
        );
    }

    return (
        <>
            <Modal.Header>{t(`FORM_BOOKANDSEND_BULK_REMINDER_HEADER`)}</Modal.Header>

            <Modal.Content>
                <SendAsEmail
                    title={t(`FORM_BOOKANDSEND_BULK_REMINDER_TEXT`, {
                        COUNT: formData.remindersToSendCount,
                    })}
                />

                <EmailOptionsDrawerToggle
                    drawerVisible={drawer.visible}
                    onShowDrawer={drawer.show}
                    onToggleDrawer={drawer.toggle}
                />

                <div className="mt-1">
                    <EmailTemplatePicker templates={formData.templates} />
                </div>
                <div className="book-and-send-dialog__messages">
                    <DisplayMessages messages={requiredFieldValidationMessages} />
                </div>

                <div className="book-and-send-dialog__options">
                    <Field.Checkbox
                        name="emailOptions.showPdf"
                        text={t`FORM_BOOKANDSEND_SHOW_REMINDER`}
                        disabled={formData.bookAllBatches}
                    />
                </div>

                <Field.Hidden name="sendByType" value={Number(SendByType.undefined)} />
            </Modal.Content>

            <Modal.Footer>
                <Group>
                    <Button onClick={onBookOnlyAction}>{t`FORM_BOOKANDSEND_BULK_REMINDER_BOOKONLY`}</Button>
                    <Button
                        className="book-and-send-dialog__action-button"
                        appearance="primary"
                        type="submit"
                        disabled={!!requiredFieldValidationMessages.length}
                        onClick={() => onBookAndSend(false)}>
                        {t`FORM_BOOKANDSEND_BULK_REMINDER_BOOKANDSEND`}
                    </Button>
                </Group>
            </Modal.Footer>
        </>
    );
}
